import React from 'react';
import { Select, type SelectProps, ConfigProvider } from 'antd';
import commonStyles from '../../style/components/common.module.scss';
import { createPrimaryTheme, createSecondaryTheme } from '../../theme/antd-theme';

export type DropdownOption = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

export type DropdownTheme = 'primary' | 'secondary';

interface CustomDropdownProps {
  options: DropdownOption[];
  label?: string;
  theme?: DropdownTheme;
  error?: boolean;
  errorMessage?: string;
  multiple?: boolean;
  onChange?: (value: any) => void;
  required?: boolean;
}

interface DropdownInputProps extends Omit<SelectProps, 'options' | 'onChange' | 'mode'>, CustomDropdownProps {}

const DropdownInput: React.FC<DropdownInputProps> = ({
  options,
  label,
  theme = 'primary',
  error = false,
  errorMessage,
  multiple = false,
  className = '',
  onChange,
  disabled = false,
  required = false,
  ...props
}) => {
  const getThemeConfig = () => {
    switch (theme) {
      case 'secondary':
        return createSecondaryTheme();
      default:
        return createPrimaryTheme();
    }
  };

  const handleChange = (value: string | number | Array<string | number> | undefined) => {
    if (value === undefined || value === null) {
      onChange?.(undefined);
    } else if (multiple && Array.isArray(value)) {
      onChange?.(value.map((v) => String(v)));
    } else {
      onChange?.(String(value));
    }
  };

  return (
    <ConfigProvider theme={getThemeConfig()}>
      <div className={commonStyles.container}>
        {label && (
          <label className={commonStyles.label}>
            {label} {required && <span style={{ color: 'var(--color-success)' }}>*</span>}
          </label>
        )}

        <Select
          mode={multiple ? 'multiple' : undefined}
          options={options}
          onChange={handleChange}
          disabled={disabled}
          allowClear
          showSearch
          optionFilterProp="label"
          status={error ? 'error' : undefined}
          {...props}
        />

        {error && errorMessage && <span className={commonStyles.errorMessage}>{errorMessage}</span>}
      </div>
    </ConfigProvider>
  );
};

export default DropdownInput;

